import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { normalizedPreflightTenantId, preflightKeys } from "@/lib/queries";
import { updateReportMarket } from "../api/preflight.api";
import {
  bumpMarketReportCount,
  invalidateMarketAggregates,
} from "../lib/market-cache";
import { patchReportInCaches } from "../lib/report-cache";
import type {
  PreflightMarket,
  PreflightReportSummary,
} from "../types/preflight.types";

export function useUpdateReportMarket(
  report: Pick<PreflightReportSummary, "id" | "market">,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const tenantId = normalizedPreflightTenantId(activeTenantId);

  return useMutation({
    mutationFn: (nextMarket: PreflightMarket) =>
      updateReportMarket(report.id, nextMarket.id),
    onMutate: (nextMarket) => {
      const previousMarket = report.market;
      patchReportInCaches(queryClient, report.id, { market: nextMarket });
      if (previousMarket?.id && previousMarket.id !== nextMarket.id) {
        bumpMarketReportCount(queryClient, tenantId, previousMarket.id, -1);
      }
      if (!previousMarket || previousMarket.id !== nextMarket.id) {
        bumpMarketReportCount(queryClient, tenantId, nextMarket.id, 1);
      }
      return { previousMarket };
    },
    onError: (_error, nextMarket, context) => {
      if (context) {
        patchReportInCaches(queryClient, report.id, {
          market: context.previousMarket,
        });
        const previousId = context.previousMarket?.id;
        if (previousId !== nextMarket.id) {
          bumpMarketReportCount(queryClient, tenantId, nextMarket.id, -1);
          if (previousId) {
            bumpMarketReportCount(queryClient, tenantId, previousId, 1);
          }
        }
      }
      toast.error(t("preflight.toast.changeMarketError"));
    },
    onSuccess: () => {
      invalidateMarketAggregates(queryClient, tenantId);
      void queryClient.invalidateQueries({ queryKey: preflightKeys.lists() });
      toast.success(t("preflight.toast.marketChanged"));
    },
    meta: { skipGlobalError: true },
  });
}
